/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo } from "react";
import { Card } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { Row } from "react-table";
import { useStateMachine } from "little-state-machine";

import "./Quotes.scss";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import { useGetQuoteList } from "api/queries/quotes/useGetQuoteList";
import Filter from "components/organisms/Filter/Filter";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import { getDateParams, getObjectByKeys } from "helpers/commonHelpers";
import usePermissions from "hooks/usePermissions";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import ListViewTour from "components/tours/listView/listViewTour";
import { updateShipmentIdAndTitle } from "store/store";
import useQuoteFilters from "hooks/filters/useQuoteFilters";
import useColumnOrderPersistor from "hooks/useColumnOrderPersistor";
import getQuoteListingColumns from "./QuoteColumns";
import { QuoteListingAdditionalColumns } from "../../../constants";

const DISPLAY_STATUS = {
  ALL: "",
  OPEN: "open_for_quotations",
  AWAITING_ACTION: "awaiting_action",
  APPROVAL_PENDING: "approval_pending",
  DRAFTS: "draft",
  SYSTEM_DRAFTS: "system_draft",
  ARCHIVED: "archived",
};

function QuotesListing() {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentLocations = useSortedPaymentLocations();
  const paymentLocationsHashMap = useMemo<Record<string, any>>(
    () => getObjectByKeys(paymentLocations, "value"),
    [paymentLocations],
  );
  const { actions } = useStateMachine({ updateShipmentIdAndTitle });
  const { config: filterConfig } = useQuoteFilters();
  const { persistedColumnOrder, onColumnOrderChange } = useColumnOrderPersistor({ moduleKey: "quote_request" });
  const status: string = searchParams.get("display_status") || "";
  const { preferred_timezone } = JSON.parse(permissions || "{}");
  const can_consolidate_orders = JSON.parse(permissions || "{}")?.permissions?.quoting?.can_consolidate_orders ?? false;

  useEffect(() => {
    if (!JSON.parse(permissions || "{}").permissions.quoting.view_quote_requests) navigate("/access-denied");
  }, []);

  const columns = useMemo(
    () =>
      getQuoteListingColumns(
        intl,
        status,
        paymentLocationsHashMap,
        JSON.parse(permissions || "{}").permissions?.quoting?.create_quote_requests,
        navigate,
        preferred_timezone,
        can_consolidate_orders,
        false,
      ),
    [status, paymentLocationsHashMap, can_consolidate_orders],
  );

  const getHiddenColumns = () => {
    const hiddenColumns = localStorage.getItem("quote_requests_hidden_columns");
    if (hiddenColumns) {
      return JSON.parse(hiddenColumns);
    }
    localStorage.setItem("quote_requests_hidden_columns", JSON.stringify(QuoteListingAdditionalColumns));
    return QuoteListingAdditionalColumns;
  };

  const { instance, loading, totalData } = useTableQueryRequest(useGetQuoteList, columns, {
    hiddenColumns: getHiddenColumns(),
    columnOrder: persistedColumnOrder,
    params: (tableInstance: any) => ({
      display_status: status,
      ...(![DISPLAY_STATUS.DRAFTS, DISPLAY_STATUS.SYSTEM_DRAFTS].includes(status) && {
        properties: QuoteListingAdditionalColumns.join(","),
      }),
      ...(tableInstance?.state?.sortBy?.[0]?.id === "location" && {
        sort_by: `payment_location:${tableInstance?.state?.sortBy?.[0]?.desc ? "DESC" : "ASC"}`,
      }),
    }),
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.display_status?.length && { display_status: globalFilter.display_status.join(",") }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location.join(",") }),
      ...(globalFilter.freight_mode?.length && { freight_modes: globalFilter.freight_mode.join(",") }),
      ...(globalFilter.pickup_date?.type && getDateParams(globalFilter.pickup_date, "pickup_date")),
      ...(globalFilter.pickup_city?.length && { pickup_city: globalFilter.pickup_city }),
      ...(globalFilter.delivery_city?.length && { delivery_city: globalFilter.delivery_city }),
      ...(globalFilter.delivery_date?.type && getDateParams(globalFilter.delivery_date, "delivery_date")),
      ...(globalFilter.created_by?.length && { created_by: globalFilter.created_by }),
      ...(globalFilter.no_quotations_received?.length && { no_quotations_received: 1 }),
    }),
  });

  useEffect(() => {
    if (instance.state.globalFilter.display_status?.length) {
      searchParams.delete("display_status");
      setSearchParams(searchParams);
    }
  }, [instance.state.globalFilter.display_status]);

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("display_status");
    } else {
      searchParams.set("display_status", eventKey || "");
    }
    setSearchParams(searchParams);
    instance.setGlobalFilter((prev: any) => ({
      ...prev,
      display_status: [],
    }));
  };

  const handleRowClick = (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.quote_request_reference;
    const title = row.original?.quote_request_title;
    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });

    const isDraft = [DISPLAY_STATUS.DRAFTS, DISPLAY_STATUS.SYSTEM_DRAFTS, "drafts"].includes(status);
    if ((e.ctrlKey || e.metaKey) && !isDraft) window.open(`/shipment/${reference}`, "_blank");
    else if (isDraft) {
      navigate(`/quote-request?draft_id=${reference}`);
    } else navigate(`/shipment/${reference}`, { state: { data: row.original?.created_by } });
  };

  return (
    <>
      <ListViewTour />
      <Card body className="shadow-none quote-container">
        <div className="d-flex flex-wrap align-items-center mb-4">
          <h3 className="mr-3 text-capitalize">
            <FormattedMessage id="general.quoteRequests" defaultMessage="Quote requests" />
          </h3>
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            filterKey="query"
            eventContext="Quote Request List"
          />
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={loading}
          onRowClick={handleRowClick}
          onColumnOrderChange={onColumnOrderChange}
          headerLeft={
            <PillTabs
              id="quote-statuses"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                { eventKey: DISPLAY_STATUS.ALL, title: intl.formatMessage({ id: "tabs.all", defaultMessage: "All" }) },
                {
                  eventKey: DISPLAY_STATUS.OPEN,
                  title: intl.formatMessage({ id: "tabs.open", defaultMessage: "Open" }),
                },
                {
                  eventKey: DISPLAY_STATUS.AWAITING_ACTION,
                  title: intl.formatMessage({ id: "tabs.pendingBooking", defaultMessage: "Pending Booking" }),
                },
                {
                  eventKey: DISPLAY_STATUS.APPROVAL_PENDING,
                  title: intl.formatMessage({ id: "tabs.pendingApproval", defaultMessage: "Pending Approval" }),
                },
                {
                  eventKey: DISPLAY_STATUS.DRAFTS,
                  title: intl.formatMessage({ id: "tabs.drafts", defaultMessage: "Drafts" }),
                },
                {
                  eventKey: DISPLAY_STATUS.SYSTEM_DRAFTS,
                  title: intl.formatMessage({ id: "tabs.systemDrafts", defaultMessage: "System Drafts" }),
                },
                {
                  eventKey: DISPLAY_STATUS.ARCHIVED,
                  title: intl.formatMessage({ id: "tabs.archived", defaultMessage: "Archived" }),
                },
              ]}
            />
          }
          headerRight={
            <div className="d-flex align-items-center">
              <Filter
                presetType="quote-request"
                eventContext="Quote Request List"
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                config={filterConfig}
              />
              <div className="ml-3">
                <ColumnFilter
                  columns={instance.allColumns}
                  eventContext="Quote Request List"
                  listViewName="quote_requests"
                />
              </div>
            </div>
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default QuotesListing;
